import React, { useState } from "react";
import Select, { MultiValue } from "react-select";
import axiosInstance from "../../axiosinstance";
import Toaster from "../../context/Toaster";
import { MINISTRY_EMAIL } from "../../utils/links";
import CountrySelect from "../country-select/CountrySelect";
import FormSubmittedModal from "./FormSubmittedModal";
import "./ContactForm.css";

export type ContactType = "general" | "partnership" | "outreach" | "volunteer";

type Option = { value: string; label: string };

const SUBJECTS: Record<ContactType, string> = {
  general: "General enquiry",
  partnership: "Partnership & booking request",
  outreach: "Relief & outreach request",
  volunteer: "Volunteer application",
};

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]!));

// The API has no contact endpoint, so messages reach the ministry inbox through
// POST /mail/send-email (to, subject, html)
const contactEmail = (type: ContactType, fields: [string, string][]) => {
  const rows = fields
    .filter(([, value]) => value.trim())
    .map(
      ([label, value]) =>
        `<tr><td style="padding:6px 12px 6px 0;color:#666;vertical-align:top"><b>${label}</b></td>` +
        `<td style="padding:6px 0;white-space:pre-wrap">${escapeHtml(value)}</td></tr>`
    )
    .join("");
  return {
    to: MINISTRY_EMAIL,
    subject: `Website: ${SUBJECTS[type]}`,
    html: `<h2 style="font-family:sans-serif">${SUBJECTS[type]}</h2><table style="font-family:sans-serif;font-size:14px">${rows}</table>`,
  };
};

const ministryAreas: Option[] = [
  "Prayer & Intercession",
  "Worship & Music",
  "Media & Technology",
  "Outreach & Missions",
  "Hospitality & Welcome",
  "Children & Youth",
  "Events & Conferences",
  "Administration",
].map((area) => ({ value: area, label: area }));

interface ContactFormProps {
  type: ContactType;
  messagePlaceholder: string;
  // Volunteer form asks which ministry areas the person is interested in
  showInterests?: boolean;
  onSuccess?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  type,
  messagePlaceholder,
  showInterests = false,
  onSuccess,
}) => {
  const { showToast } = Toaster();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [interests, setInterests] = useState<MultiValue<Option>>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !message.trim()) {
      showToast("Please fill in your name, email, and message.", {
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post(
        "/mail/send-email",
        contactEmail(type, [
          ["Name", name],
          ["Email", email],
          ["Phone", phone],
          ["Country", country],
          ["Areas of interest", showInterests ? interests.map((i) => i.label).join(", ") : ""],
          ["Message", message],
        ])
      );
      setName("");
      setEmail("");
      setPhone("");
      setCountry("");
      setInterests([]);
      setMessage("");
      // Inside ContactFormModal the parent swaps its dialog for the confirmation
      if (onSuccess) onSuccess();
      else setSubmitted(true);
    } catch (error: any) {
      const data = error?.response?.data;
      showToast(data?.error || data?.message || "Something went wrong. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <form className="contact-form" onSubmit={handleSubmit}>
      <label className="contact-form-label">
        <span>
          Full Name <span className="contact-form-required">*</span>
        </span>
        <input
          type="text"
          className="contact-form-input"
          placeholder="Enter your full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>

      <label className="contact-form-label">
        <span>
          Email Address <span className="contact-form-required">*</span>
        </span>
        <input
          type="email"
          className="contact-form-input"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>

      <label className="contact-form-label">
        <span>
          Phone Number <span className="contact-form-optional">(Optional)</span>
        </span>
        <input
          type="tel"
          className="contact-form-input"
          placeholder="Enter your phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </label>

      <label className="contact-form-label">
        Country
        <div className="contact-form-select-wrap">
          <CountrySelect
            className={`contact-form-input contact-form-select${
              country ? "" : " is-placeholder"
            }`}
            value={country}
            onChange={setCountry}
          />
        </div>
      </label>

      {showInterests && (
        <div className="contact-form-label">
          What Areas of Ministry Interests you?
          <Select
            isMulti
            options={ministryAreas}
            value={interests}
            onChange={setInterests}
            placeholder="Select all areas you’re interested in"
            classNamePrefix="contact-interests"
            closeMenuOnSelect={false}
            aria-label="Areas of ministry"
          />
        </div>
      )}

      <label className="contact-form-label">
        <span>
          Message <span className="contact-form-required">*</span>
        </span>
        <textarea
          className="contact-form-input contact-form-textarea"
          placeholder={messagePlaceholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
        />
      </label>

      <button type="submit" className="contact-form-submit" disabled={loading}>
        {loading ? "Sending..." : "Send My Message"}
      </button>
    </form>

    {submitted && <FormSubmittedModal type={type} onClose={() => setSubmitted(false)} />}
    </>
  );
};

export default ContactForm;
